import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@theme-ui/components';

import { Alert, AlertType } from '@app/components/Alert';
import LoginLink from '@app/components/LoginLink';

import type { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant } from '../../hook/__generated__/PricingPageShopProduct';
import type { UpgradeVariants } from '../../hook/useUpgradePaths';
import { LicenseCardVariant } from '../LicenseCard/LicenseCard.constants';
import { PACKAGE_TYPE } from '../StockMessage';
import messages from './ProductUpgradeMessage.messages';
import QualifyingProductMessage from './QualifyingProductMessage';
import RequiredProductsWarning from './RequiredProductsWarning';
import useCardVariant from './useCardVariant';
import useLicenseSelectorMessages from './useLicenseSelectorMessages';

export type LicenseCardGridProps = {
  hasBestOption: boolean;
  variantLicenses: (Variant | null)[];
  getCardVariant: (variant: Variant) => LicenseCardVariant;
  selectedVariant: Variant | null;
  setSelectedVariant: (variant: Variant | null) => void;
};

interface LicenseSelectorProps {
  variants: (Variant | null)[];
  selectedVariant: Variant | null;
  setSelectedVariant: (variant: Variant | null) => void;
  upgradeVariants: UpgradeVariants;
  isAuthenticated?: boolean;
  children?: ({
    hasBestOption,
    variantLicenses,
    getCardVariant,
  }: LicenseCardGridProps) => React.ReactNode;
}

const LicenseSelector = ({
  variants,
  selectedVariant,
  setSelectedVariant,
  upgradeVariants,
  isAuthenticated,
  children,
}: LicenseSelectorProps) => {
  const isSingleLicenseProduct = variants?.length === 1;

  const downloadableVariants = variants?.filter(
    (variant) => variant?.attributes?.[1]?.adminLabel === PACKAGE_TYPE.DL,
  );

  const boxedProductVariants = variants?.filter(
    (variant) => variant?.attributes?.[1]?.adminLabel === PACKAGE_TYPE.BOX,
  );

  const configurableVariants = downloadableVariants?.length
    ? (downloadableVariants as Variant[])
    : boxedProductVariants?.length
      ? (boxedProductVariants as Variant[])
      : [];

  const variantLicenses = isSingleLicenseProduct
    ? variants
    : configurableVariants;

  const upgradeVariantsIds = Object.keys(upgradeVariants);

  const sdbsId =
    selectedVariant?.product?.sdbsId ?? selectedVariant?.product?.sdbsId;

  const selectedVariantHasUpgrade = upgradeVariantsIds.includes(sdbsId || '');

  const {
    selectedVariantHasRequiredProducts,
    ownershipMessagesByVariant,
    variantsWithRequiredBaseProducts,
  } = useLicenseSelectorMessages({
    variants: variantLicenses,
    selectedVariant,
  });

  const { getCardVariant } = useCardVariant({
    variants: variantLicenses,
    upgradeVariants,
    isAuthenticated,
    variantsWithRequiredBaseProducts,
  });

  const hasBestOption = variantLicenses.some(
    (variant) =>
      variant && getCardVariant(variant) === LicenseCardVariant.Recommendation,
  );

  return (
    <>
      {!isSingleLicenseProduct && (
        <Box mb={3}>
          {variantLicenses && isAuthenticated ? (
            <QualifyingProductMessage
              variants={variantLicenses}
              selectedVariant={selectedVariant}
              selectVariant={setSelectedVariant}
              upgradeVariants={upgradeVariants}
            />
          ) : (
            <Alert type={AlertType.Info}>
              <FormattedMessage
                {...messages.loginPrompt}
                values={{
                  loginPromptLink: (
                    <LoginLink>
                      <FormattedMessage {...messages.loginPromptLink} />
                    </LoginLink>
                  ),
                }}
              />
            </Alert>
          )}
        </Box>
      )}

      <Flex sx={{ width: '100%', flexDirection: 'column' }}>
        {children &&
          children({
            hasBestOption,
            variantLicenses,
            getCardVariant,
            selectedVariant,
            setSelectedVariant,
          })}

        {selectedVariantHasRequiredProducts && !selectedVariantHasUpgrade && (
          <Box mt={20}>
            <RequiredProductsWarning messages={ownershipMessagesByVariant} />
          </Box>
        )}
      </Flex>
    </>
  );
};

export default LicenseSelector;
